import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api, formatApiError } from "@/lib/api";
import { Heart, Shield, GraduationCap, Users, UtensilsCrossed, Sprout, Loader2, CheckCircle2, IndianRupee, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CAMPAIGNS = [
  { id: "general", label: "General Donation", icon: Heart, desc: "Support the foundation's overall mission", color: "from-rose-500 to-pink-600" },
  { id: "child-education", label: "Child Education", icon: GraduationCap, desc: "Support underprivileged children's education", color: "from-blue-600 to-indigo-700" },
  { id: "healthcare", label: "Healthcare Aid", icon: Heart, desc: "Fund medical camps & treatments", color: "from-red-500 to-rose-600" },
  { id: "food", label: "Food Distribution", icon: UtensilsCrossed, desc: "Feed families in need", color: "from-amber-500 to-orange-600" },
  { id: "women", label: "Women Empowerment", icon: Users, desc: "Support women skill programs", color: "from-violet-600 to-fuchsia-600" },
  { id: "rural", label: "Rural Development", icon: Sprout, desc: "Build rural infrastructure & water points", color: "from-emerald-600 to-teal-700" },
];

const PRESETS = [500, 1000, 2500, 5000, 10000, 25000];

export default function Donate() {
  const [hp, setHp] = useState(null);
  const [campaign, setCampaign] = useState("general");
  const [amount, setAmount] = useState(1000);
  const [form, setForm] = useState({ donor_name: "", email: "", phone: "", pan: "", address: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/public/homepage").then((r) => setHp(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1) { toast.error("Enter a valid amount"); return; }
    if (!form.donor_name || !form.email || !form.phone) { toast.error("Fill name, email and phone"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        amount: Number(amount),
        campaign: CAMPAIGNS.find((c) => c.id === campaign)?.label || "General Donation",
        return_origin: window.location.origin,
      };
      const { data } = await api.post("/donations/initiate", payload);
      toast.info("Redirecting to PayU secure gateway...");
      const formEl = document.createElement("form");
      formEl.method = "POST";
      formEl.action = data.payu.action;
      for (const [k, v] of Object.entries(data.payu)) {
        if (k === "action") continue;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        formEl.appendChild(input);
      }
      document.body.appendChild(formEl);
      formEl.submit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const active = CAMPAIGNS.find((c) => c.id === campaign);
  const ActiveIcon = active?.icon || Heart;

  return (
    <div className="min-h-screen bg-slate-100">
      <PublicNavbar />

      {/* Hero (dark so white text is visible) */}
      <section className="relative bg-[#0B1120] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 25% 30%, #1E3A8A 0%, transparent 45%), radial-gradient(circle at 75% 70%, #F59E0B 0%, transparent 40%)" }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500 text-slate-900 text-xs font-black shadow-lg">
            <Shield size={13} /> 80G Certified · Tax Deductible
          </div>
          <h1 className="heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black text-white">Donation Form</h1>
          <p className="mt-4 text-slate-300 font-semibold">Secure payment powered by PayU · Get instant email confirmation</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8">
        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-10 grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            {/* Campaign */}
            <div>
              <label className="heading text-xl font-black text-slate-900">Select a Campaign</label>
              <p className="text-sm text-slate-500 mt-1">Choose a cause close to your heart.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {CAMPAIGNS.map((c) => {
                  const I = c.icon;
                  const active = campaign === c.id;
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => setCampaign(c.id)}
                      className={`text-left p-4 rounded-xl border-2 transition ${active ? "border-brand bg-blue-50 ring-2 ring-brand/20" : "border-slate-200 hover:border-brand/40 bg-white"}`}
                      data-testid={`campaign-${c.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${active ? `bg-gradient-to-br ${c.color} text-white shadow-md` : "bg-slate-100 text-slate-500"}`}>
                          <I size={18} />
                        </div>
                        <div>
                          <div className="font-black text-slate-900">{c.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{c.desc}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amount */}
            <div className="mt-10">
              <label className="heading text-xl font-black text-slate-900">Donation Amount <span className="text-brand">(₹)</span></label>
              <p className="text-sm text-slate-500 mt-1">Every rupee creates real impact.</p>
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
                {PRESETS.map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setAmount(p)}
                    className={`py-3 rounded-xl font-black text-sm transition ${amount === p ? "bg-brand text-white shadow-lg scale-105" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
                    data-testid={`donate-preset-${p}`}
                  >
                    ₹{p.toLocaleString()}
                  </button>
                ))}
              </div>
              <div className="mt-3 relative">
                <IndianRupee size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full pl-11 pr-4 py-4 rounded-xl border-2 border-slate-200 focus:border-brand outline-none text-lg font-black text-slate-900"
                  placeholder="Enter custom amount"
                  data-testid="donate-amount-input"
                />
              </div>
            </div>

            {/* Donor Details */}
            <div className="mt-10">
              <label className="heading text-xl font-black text-slate-900">Donor Details</label>
              <p className="text-sm text-slate-500 mt-1">Required for issuing your 80G tax receipt.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <Input label="Full Name *" value={form.donor_name} onChange={(v) => setForm({ ...form, donor_name: v })} required test="donor-name-input" />
                <Input label="Email Address *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required test="donor-email-input" />
                <Input label="Phone Number *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required test="donor-phone-input" />
                <Input label="PAN (for 80G receipt)" value={form.pan} onChange={(v) => setForm({ ...form, pan: v.toUpperCase() })} test="donor-pan-input" />
                <div className="sm:col-span-2">
                  <Input label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} test="donor-address-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-600">Message (optional)</label>
                  <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand outline-none resize-none text-slate-900" data-testid="donor-message-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Summary — dark card for strong contrast */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-3xl bg-gradient-to-br from-[#0B1120] via-[#1E3A8A] to-[#172b66] text-white p-7 shadow-2xl overflow-hidden relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-amber-500/20 rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${active?.color || 'from-rose-500 to-pink-600'} flex items-center justify-center`}>
                    <ActiveIcon size={16} className="text-white" />
                  </div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-amber-300">{active?.label}</div>
                </div>
                <div className="heading text-5xl font-black mt-5 text-white">₹{Number(amount).toLocaleString("en-IN")}</div>
                <div className="text-sm text-blue-100 mt-1 font-semibold">Your Contribution</div>
                <hr className="border-white/20 my-5" />
                <div className="space-y-2.5 text-sm">
                  <Row label="Impact area" value={active?.label} />
                  <Row label="80G Tax Benefit" value="Yes" highlight />
                  <Row label="Payment" value="PayU Secure" />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-7 w-full bg-[#F59E0B] hover:bg-[#D97706] active:scale-[0.98] text-white font-black rounded-xl py-4 flex items-center justify-center gap-2 shadow-lg disabled:opacity-60 text-lg transition"
                  data-testid="donate-submit-button"
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <Heart size={18} className="fill-white" />}
                  {loading ? "Redirecting..." : `Donate ₹${Number(amount).toLocaleString("en-IN")}`}
                </button>
                <div className="mt-5 space-y-2 text-xs text-white font-semibold">
                  <div className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-400" /> 80G Reg: AAOCP5008QF20251</div>
                  <div className="flex items-center gap-2"><Lock size={13} className="text-emerald-400" /> Secure PayU Gateway</div>
                  <div className="flex items-center gap-2"><Sparkles size={13} className="text-emerald-400" /> Instant Email Receipt</div>
                </div>
              </div>
            </div>

            {/* Why give */}
            <div className="mt-5 p-5 rounded-2xl bg-amber-50 border border-amber-200">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-[#D97706]">Why give to PMR?</div>
              <ul className="mt-3 text-sm text-slate-700 space-y-2">
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Registered Section 8 NGO</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> 10,000+ lives impacted</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> Audited annually</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> 100% utilisation guarantee</li>
              </ul>
            </div>
          </aside>
        </form>

        {/* Legal strip */}
        <div className="mt-6 text-center text-xs text-slate-500">
          By donating you agree to our <Link to="/terms" className="text-brand font-bold">Terms</Link>, <Link to="/privacy" className="text-brand font-bold">Privacy Policy</Link> and <Link to="/refund" className="text-brand font-bold">Refund Policy</Link>.
        </div>
      </section>

      <PublicFooter contact={hp} />
      <WhatsAppButton number={hp?.whatsapp_number || "919155561892"} />
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, test }) {
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-600">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand outline-none text-slate-900" data-testid={test} />
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-blue-100 font-semibold">{label}</span>
      <span className={`font-black ${highlight ? "text-emerald-400" : "text-white"}`}>{value}</span>
    </div>
  );
}
